/* Common Imports */

import { styled, useMediaQuery, useTheme } from "@mui/system";
import React from "react";

/* Redux & Type Imports */

import { ObjectId } from "bson";
import { NextRouter } from "next/router";
import StateInterface from "../../../redux-magic/state-interface";
import { createNewBroadcastThunk, setBroadcastFormThunk } from "../../../redux-magic/store";

/* Component Imports */

import {
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Typography,
} from "@mui/material";
import Commission from "./Commission";
import AgentScreen from "./NoOfAgentScreen";
import Notes from "./Notes";
import SuccessScreen from "./SuccessScreen";
import Summary from "./Summary";

/* Icon Imports */

import { ArrowBack, Close, SendOutlined } from "@mui/icons-material";
import BeegruButton from "../../common-components/buttons/BeegruButton";

/* Styled Components */

const CustomDialog = styled(Dialog)(({ theme }) => ({
	"& .MuiDialog-paper": {
		[theme.breakpoints.down("sm")]: {
			borderRadius: "0rem",
			minHeight: "100%",
		},
		minHeight: "820px",
		maxHeight: "100%",
		borderRadius: "1rem",
		background: theme.palette.background.paper,
		position: "relative",
	},
}));

const CustomDialogTitle = styled(DialogTitle)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	fontSize: "1.25rem",
	fontWeight: 500,
	lineHeight: "106%",
	background: theme.palette.background.paper,
	gap: "0rem",
	padding: "1rem ",
	borderRadius: "1rem 1rem 0rem 0rem",
	"&+.MuiDialogContent-root": {
		padding: "0px",
	},
}));

const CustomDialogContent = styled(DialogContent)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	height: "100%",
	gap: "0.5rem",
	padding: "0rem 0rem 0rem 0rem",
	background: theme.palette.background.paper,
	overflow: "scroll",
	flex: 1,
	scrollbarWidth: "none",
	// Remove default MUI padding
	"&.MuiDialogContent-root": {
		padding: "0px",
		height: "100%",
	},
	// Remove padding from subsequent dialog content
	"&+.MuiDialogContent-root": {
		padding: "0px",
	},
}));

const CustomDialogActions = styled(DialogActions)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	gap: "0.5rem",
	padding: "1rem",
	background: theme.palette.background.paper,
	borderRadius: "1rem 1rem 1rem 1rem",
	"& > :not(:first-of-type)": {
		marginLeft: 0,
	},
}));

const RowContainer = styled("div")({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
});

const TitleText = styled(Typography)({
	fontWeight: 600,
});

export default function CreateNewBroadcastModal({
	dispatch,
	broadcast,
	transactionType,
	propertyId,
	business_profile_id,
	session,
	profile_context,
	propertyStatus,
	setOpen,
	open,
	router,
}: {
	business_profile_id: string | ObjectId | undefined | null;
	dispatch: Function;
	broadcast: StateInterface["broadcast_data"] | undefined | null;
	transactionType: string;
	propertyId: ObjectId | string;
	session: StateInterface["redux_session"];
	profile_context: any;
	propertyStatus: string;
	setOpen: Function;
	open: boolean;
	router: NextRouter;
}) {
	const theme = useTheme();

	const smDevice: boolean = useMediaQuery(theme.breakpoints.down("sm"));

	/* Translations */

	const localLang = {
		skip: router.locale === "ar-AE" ? "تخطي" : router.locale === "kn-IN" ? "ಬಿಟ್ಟುಹೋಗಿ" : "Skip",
		next: router.locale === "ar-AE" ? "التالي" : router.locale === "kn-IN" ? "ಮುಂದೆ" : "Next",
		network_of_agents:
			router.locale === "ar-AE" ? "شبكة الوكلاء" : router.locale === "kn-IN" ? "ಏಜೆಂಟ್‌ಗಳ ಜಾಲ" : "Network of Agents",
		brokerage: router.locale === "ar-AE" ? "الوساطة" : router.locale === "kn-IN" ? "ಮಧ್ಯಸ್ಥಿಕೆ" : "Brokerage",
		notes_optional:
			router.locale === "ar-AE"
				? "ملاحظات (اختياري)"
				: router.locale === "kn-IN"
					? "ಸೂಚನೆಗಳು (ಐಚ್ಛಿಕ)"
					: "Notes (Optional)",
		preview: router.locale === "ar-AE" ? "معاينة" : router.locale === "kn-IN" ? "ಪೂರ್ವವೀಕ್ಷಣೆ" : "Preview",
		select_brokerage:
			router.locale === "ar-AE"
				? "اختر الوساطة"
				: router.locale === "kn-IN"
					? "ಮಧ್ಯಸ್ಥಿಕೆಯನ್ನು ಆಯ್ಕೆಮಾಡಿ"
					: "Select brokerage",
		confirm: router.locale === "ar-AE" ? "تأكيد" : router.locale === "kn-IN" ? "ದೃಢೀಕರಿಸಿ" : "Confirm",
		send_broadcast:
			router.locale === "ar-AE" ? "إرسال البث" : router.locale === "kn-IN" ? "ಪ್ರಸಾರವನ್ನು ಕಳುಹಿಸಿ" : "Send Broadcast",
		done: router.locale === "ar-AE" ? "تم" : router.locale === "kn-IN" ? "ಮುಗಿಯಿತು" : "Done",
	};

	const [page, setPage] = React.useState<number>(1);

	const [sendLoading, setSendLoading] = React.useState<boolean>(false);

	React.useEffect(() => {
		const handlePopState = (event: PopStateEvent) => {
			event.preventDefault();
			setPage(page - 1);
		};

		window.addEventListener("popstate", handlePopState);
		return () => window.removeEventListener("popstate", handlePopState);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	type CompProps = {
		dispatch: Function;
		broadcast: StateInterface["broadcast_data"] | undefined | null;
		transactionType: string;
		propertyId: ObjectId | string;
		userId: string | ObjectId;
		business_profile_id: string | ObjectId | undefined | null;
		close: Function;
		setPage: Function;
		session: StateInterface["redux_session"];
		profile_context: any;
		propertyStatus: string;
		router: NextRouter;
		loading: boolean;
	};

	let Comp: React.ComponentType<CompProps> = () => (
		<div
			style={{
				padding: "0rem",
				width: "100%",
				height: "100%",
			}}
		></div>
	);

	let ButtonLabel = "";

	let title = "";

	switch (page) {
		case 1:
			Comp = AgentScreen;
			ButtonLabel = localLang.select_brokerage;
			title = localLang.network_of_agents;
			break;
		case 2:
			Comp = Commission;
			ButtonLabel = localLang.confirm;
			title = localLang.select_brokerage;
			break;
		case 3:
			Comp = Notes;
			ButtonLabel = localLang.next;
			title = localLang.notes_optional;
			break;
		case 4:
			Comp = Summary;
			ButtonLabel = localLang.send_broadcast;
			title = localLang.preview;
			break;
		case 5:
			Comp = SuccessScreen;
			ButtonLabel = localLang.done;
			title = "Success";
			break;
		default:
			Comp = AgentScreen;
	}

	const handleClose = () => {
		setOpen(false);
		setPage(1);
		dispatch(
			setBroadcastFormThunk({
				commission_offered: transactionType === "rent" ? 15 : 2,
				no_of_agents: broadcast?.no_of_agents || 0,
				notes: "",
			}),
		);
	};

	const handleClick = async () => {
		if (page !== 5) {
			if (page === 4) {
				setSendLoading(true);
				await dispatch(
					createNewBroadcastThunk({
						creator_is_business_profile: profile_context.is_business_profile,
						creator_user_id: session ? (session.user ? session.user.dbUser._id : "") : "",
						creator_business_profile_id: profile_context.business_profile_id,
						property_id: propertyId,
						no_of_agents: broadcast?.no_of_agents || 0,
						commission_offered: broadcast?.commission_offered || 0,
						notes: broadcast?.notes || "",
						property_status: propertyStatus,
						sessionId: session ? (session.session_id ? session.session_id : "") : "",
					}),
				);
				setSendLoading(false);
			}

			setPage(page + 1);
		} else {
			handleClose();
		}
	};

	return (
		<CustomDialog
			maxWidth="xs"
			fullWidth={true}
			fullScreen={smDevice}
			open={open}
			onClose={handleClose}
		>
			{page !== 5 ? (
				<CustomDialogTitle>
					<RowContainer
						sx={{
							gap: "0.5rem",
						}}
					>
						{page !== 1 && page !== 5 && !sendLoading ? (
							<IconButton
								aria-label="back"
								onClick={() => {
									setPage(page - 1);
								}}
							>
								<ArrowBack fontSize="small" />
							</IconButton>
						) : null}
						<TitleText variant="body1">{title}</TitleText>
					</RowContainer>

					{!sendLoading ? (
						<IconButton
							aria-label="close"
							onClick={handleClose}
						>
							<Close fontSize="small" />
						</IconButton>
					) : null}
				</CustomDialogTitle>
			) : null}

			<CustomDialogContent>
				<Comp
					propertyStatus={propertyStatus}
					close={handleClose}
					dispatch={dispatch}
					broadcast={broadcast}
					transactionType={transactionType}
					propertyId={propertyId}
					business_profile_id={business_profile_id}
					userId={profile_context.user_id}
					setPage={setPage}
					session={session}
					profile_context={profile_context}
					router={router}
					loading={sendLoading}
				/>
			</CustomDialogContent>

			<CustomDialogActions>
				{page === 3 ? (
					<BeegruButton
						size="large"
						flavor="primary"
						variant="text"
						disabled={sendLoading}
						fullWidth
						disableRipple
						disableFocusRipple
						disableTouchRipple
						onClick={handleClick}
						sx={{
							"&.MuiButton-text": {
								color: "#621b16",
								background: "none",
								"&:hover": {
									background: "none",
								},
								"&:active": {
									background: "none",
								},
								"&:focus": {
									background: "none",
								},
							},
						}}
					>
						{localLang.skip}
					</BeegruButton>
				) : null}

				<BeegruButton
					size="large"
					flavor="primary"
					variant="contained"
					disabled={sendLoading}
					fullWidth
					disableRipple
					disableFocusRipple
					disableTouchRipple
					onClick={handleClick}
					sx={{
						"&.MuiButton-contained": {
							background: "#621b16",
							color: "#FFFFFF",
							boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
							"&:hover": {
								background: "#621b16",
								boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
							},
							"&:active": {
								background: "#621b16",
								boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
							},
							"&:focus": {
								background: "#621b16",
								boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
							},
						},
					}}
				>
					{!sendLoading ? (
						<>
							{ButtonLabel}

							{page === 4 ? (
								<SendOutlined
									sx={{
										fontSize: "1rem",
										margin: "0rem 0rem 0rem 0.5rem",
									}}
								/>
							) : null}
						</>
					) : (
						<CircularProgress
							sx={{
								color: "#FFFFFF",
							}}
						/>
					)}
				</BeegruButton>
			</CustomDialogActions>
		</CustomDialog>
	);
}
